/**
 * Element Duplication
 *
 * Duplicates elements for marquee effects and visual consistency.
 *
 * Child mode (duplicate children):
 *   <div data-hs-dupe="child" data-hs-dupe-count="2">
 *     <div class="marquee_item">Content</div>
 *   </div>
 *
 * Self mode (duplicate wrapper):
 *   <div data-hs-dupe="self" data-hs-dupe-count="3">Content</div>
 *
 * Modifiers: data-hs-dupe-hidden (aria-hidden), data-hs-dupe-no-select (disable selection).
 */
import { querySelectorAll } from '@utils';

const MAX_DUPE_COUNT = 20;
const TRANSITION_CLASS = 'hs-dupe-no-transition';

// Inject CSS once
let cssInjected = false;
function injectCSS(): void {
  if (cssInjected) return;

  const style = document.createElement('style');
  style.textContent = `
    .${TRANSITION_CLASS},
    .${TRANSITION_CLASS} * {
      transition: none !important;
      animation: none !important;
    }
  `;
  document.head.appendChild(style);
  cssInjected = true;
}

interface DupeConfig {
  attributes: {
    elements: {
      [key: string]: {
        primary: string;
        aliases?: string[];
      };
    };
  };
}

interface CleanupRecord {
  element: Element;
  mode: string;
  clones: Element[];
}

interface DupeOptions {
  hidden: boolean;
  noSelect: boolean;
}

export async function init(config: DupeConfig): Promise<{ result: string; destroy: () => void }> {
  // Inject CSS on first init
  injectCSS();

  const cleanup: { processedElements: CleanupRecord[] } = {
    processedElements: [],
  };

  // Find all elements with dupe attribute
  const dupeElements = querySelectorAll(config, 'wrapper');

  dupeElements.forEach((element) => {
    // Get dupe mode (child or self)
    const mode = element.getAttribute('data-hs-dupe');
    if (!mode || (mode !== 'child' && mode !== 'self')) {
      console.warn('[dupe] Invalid mode. Use "child" or "self":', element);
      return;
    }

    // Get dupe count (default: 1)
    const countAttr = element.getAttribute('data-hs-dupe-count');
    let count = countAttr ? parseInt(countAttr, 10) : 1;

    // Validate and cap count
    if (isNaN(count) || count < 1) {
      console.warn('[dupe] Invalid count, using default of 1:', element);
      count = 1;
    }

    if (count > MAX_DUPE_COUNT) {
      console.warn(
        `[dupe] Count of ${count} exceeds maximum of ${MAX_DUPE_COUNT}. Capping at ${MAX_DUPE_COUNT}.`
      );
      count = MAX_DUPE_COUNT;
    }

    // Get modifier options
    const options = {
      hidden: element.hasAttribute('data-hs-dupe-hidden'),
      noSelect: element.hasAttribute('data-hs-dupe-no-select'),
    };

    // Execute duplication based on mode
    if (mode === 'child') {
      dupeChild(element, count, options, cleanup);
    } else if (mode === 'self') {
      dupeSelf(element, count, options, cleanup);
    }
  });

  return {
    result: `dupe processed ${cleanup.processedElements.length} elements`,
    destroy: () => {},
  };
}

function dupeChild(
  element: Element,
  count: number,
  options: DupeOptions,
  cleanup: { processedElements: CleanupRecord[] }
): void {
  // Get first direct child
  const firstChild = element.children[0];

  // Silently skip if no child exists (e.g., empty slot waiting for CMS content)
  if (!firstChild) {
    return;
  }

  const clones: Element[] = [];

  // Create clones and append as siblings
  for (let i = 0; i < count; i++) {
    const clone = firstChild.cloneNode(true) as Element;

    // Disable transitions during insertion
    clone.classList.add(TRANSITION_CLASS);

    // Apply modifiers to clone
    applyModifiers(clone, options);

    // Append to DOM
    element.appendChild(clone);
    clones.push(clone);

    // Force reflow
    (clone as HTMLElement).offsetHeight;

    // Re-enable transitions on next frame
    requestAnimationFrame(() => {
      clone.classList.remove(TRANSITION_CLASS);
    });
  }

  // Track for cleanup
  cleanup.processedElements.push({
    element,
    mode: 'child',
    clones,
  });
}

function dupeSelf(
  element: Element,
  count: number,
  options: DupeOptions,
  cleanup: { processedElements: CleanupRecord[] }
): void {
  const parent = element.parentNode;

  if (!parent) {
    console.warn('[dupe] Element has no parent, cannot duplicate self:', element);
    return;
  }

  const clones: Element[] = [];

  // Create clones and insert as next siblings
  let referenceNode: Element | Node = element;
  for (let i = 0; i < count; i++) {
    const clone = element.cloneNode(true) as Element;

    // Disable transitions during insertion
    clone.classList.add(TRANSITION_CLASS);

    // Remove dupe attributes from clone
    clone.removeAttribute('data-hs-dupe');
    clone.removeAttribute('data-hs-dupe-count');
    clone.removeAttribute('data-hs-dupe-hidden');
    clone.removeAttribute('data-hs-dupe-no-select');

    // Apply modifiers to clone
    applyModifiers(clone, options);

    // Insert after reference node
    if (referenceNode.nextSibling) {
      parent.insertBefore(clone, referenceNode.nextSibling);
    } else {
      parent.appendChild(clone);
    }

    clones.push(clone);
    referenceNode = clone; // Next clone goes after this one

    // Force reflow
    (clone as HTMLElement).offsetHeight;

    // Re-enable transitions on next frame
    requestAnimationFrame(() => {
      clone.classList.remove(TRANSITION_CLASS);
    });
  }

  // Track for cleanup
  cleanup.processedElements.push({
    element,
    mode: 'self',
    clones,
  });
}

function applyModifiers(clone: Element, options: DupeOptions): void {
  // Add aria-hidden if requested
  if (options.hidden) {
    clone.setAttribute('aria-hidden', 'true');
  }

  // Add user-select: none if requested
  if (options.noSelect) {
    const htmlClone = clone as HTMLElement;
    htmlClone.style.userSelect = 'none';
    (htmlClone.style as unknown as { webkitUserSelect: string }).webkitUserSelect = 'none';
    (htmlClone.style as unknown as { msUserSelect: string }).msUserSelect = 'none';
  }
}
